import { useEffect, useRef, useState } from "react";
import { formatDuration } from "@/lib/timers";
import { Play, Pause, RotateCcw, Timer } from "lucide-react";

/** Chunky countdown for Cook Mode. Auto-resets whenever the seconds prop changes.
 *  Emits onComplete once when it reaches zero; expected to trigger the narrator. */
export default function StepTimer({ seconds, onComplete }) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const doneRef = useRef(false);

  // Reset whenever the step (and therefore duration) changes
  useEffect(() => {
    setRemaining(seconds);
    setRunning(false);
    doneRef.current = false;
  }, [seconds]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          setRunning(false);
          if (!doneRef.current) {
            doneRef.current = true;
            try { onComplete?.(); } catch {}
          }
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, onComplete]);

  if (seconds == null) return null;

  const pct = seconds > 0 ? Math.max(0, Math.min(100, (remaining / seconds) * 100)) : 0;
  const done = remaining === 0;

  return (
    <div data-testid="cook-timer" className="mt-6 flex flex-col items-center">
      <div className="accent-label text-[#B79256]">timer for this step</div>
      <div
        className={`mt-2 font-serif-display text-6xl sm:text-7xl leading-none tabular-nums ${done ? "text-[#4E5A3D]" : "text-[#2A2823]"}`}
        data-testid="cook-timer-value"
      >
        {formatDuration(remaining)}
      </div>
      <div className="w-full max-w-md h-2 rounded-full bg-[#B79256]/15 overflow-hidden mt-4">
        <div className="h-full bg-[#4E5A3D] transition-[width] duration-500" style={{ width: `${100 - pct}%` }} />
      </div>
      <div className="mt-5 flex flex-wrap gap-2 justify-center">
        {!done ? (
          <button
            type="button"
            data-testid="cook-timer-toggle"
            onClick={() => setRunning((r) => !r)}
            className="h-12 px-6 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] inline-flex items-center gap-2 text-base font-medium touch-manipulation active:scale-[0.98]"
          >
            {running
              ? <><Pause className="w-4 h-4" strokeWidth={2}/> Pause</>
              : <><Play className="w-4 h-4" strokeWidth={2}/> {remaining === seconds ? "Start timer" : "Resume"}</>}
          </button>
        ) : (
          <span data-testid="cook-timer-done" className="accent-label text-[#4E5A3D] inline-flex items-center gap-1.5">
            <Timer className="w-3.5 h-3.5" strokeWidth={2}/> done — next step
          </span>
        )}
        <button
          type="button"
          data-testid="cook-timer-reset"
          onClick={() => { setRemaining(seconds); setRunning(false); doneRef.current = false; }}
          className="h-12 px-5 rounded-full border border-[#B79256]/40 bg-white text-[#2A2823] hover:bg-[#B79256]/10 inline-flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" strokeWidth={1.75}/> Reset
        </button>
      </div>
    </div>
  );
}
